import { useMemo } from 'react'

import { Select, SubText, Text } from '@procurify/ui'
import { useSelector } from 'react-redux'
import {
  type SingleValueProps,
  type OptionProps,
  components as ReactSelectComponents,
} from 'react-select'

import { makeSelectUser } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { type NextApproverChoice } from '@webapp/modules/expense/entities'
import messages from './messages'

export interface IApproverChoiceSelectProps {
  // Id of the combobox input, so an `InputLabel htmlFor` names it
  inputId?: string
  choices: NextApproverChoice[]
  selectedApproverId: Nullable<number>
  onChange: (approverId: Nullable<number>) => void
}

// Typed search matches this text, so it carries the level as well as the name
const formatChoiceLabel = (choice: NextApproverChoice) =>
  choice.levelText ? `${choice.fullName} ${choice.levelText}` : choice.fullName

const ApproverOptionLabel = ({
  approver,
  userId,
}: {
  approver: NextApproverChoice
  userId: number
}) => {
  const intl = useProcurifyIntl()

  return (
    <>
      <Text autoMargin={false}>
        {formatChoiceLabel(approver)}
        {approver.id === userId
          ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
          : ''}
      </Text>
      {!!approver.approvalDelegatee && (
        <SubText color='textSecondary'>
          {intl.formatMessage(messages.delegatedToLabel, {
            user: approver.approvalDelegatee.fullName,
          })}
          {approver.approvalDelegatee.id === userId
            ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
            : ''}
        </SubText>
      )}
    </>
  )
}

const SingleValue = (
  props: SingleValueProps<NextApproverChoice, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props
  return (
    <ReactSelectComponents.SingleValue
      {...otherProps}
      className='approval-chain-approver-single-value'
    >
      <ApproverOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.SingleValue>
  )
}

const Option = (
  props: OptionProps<NextApproverChoice, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props
  return (
    <ReactSelectComponents.Option {...otherProps}>
      <ApproverOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.Option>
  )
}

export const ApproverChoiceSelect = ({
  inputId,
  choices,
  selectedApproverId,
  onChange,
}: IApproverChoiceSelectProps) => {
  const intl = useProcurifyIntl()
  const userId = useSelector(makeSelectUser()).id

  const selectComponents = useMemo(
    () => ({
      SingleValue: (
        componentProps: SingleValueProps<NextApproverChoice, false>
      ) => <SingleValue {...componentProps} userId={userId} />,
      Option: (componentProps: OptionProps<NextApproverChoice, false>) => (
        <Option {...componentProps} userId={userId} />
      ),
    }),
    [userId]
  )

  const selectedOption =
    choices.find((choice) => choice.id === selectedApproverId) ?? null

  return (
    <Select<NextApproverChoice>
      inputId={inputId}
      options={choices}
      components={selectComponents}
      placeholder={intl.formatMessage(messages.placeholderText)}
      value={selectedOption}
      getOptionLabel={formatChoiceLabel}
      getOptionValue={(opt) => opt.id.toString()}
      onChange={(option) => {
        onChange(option ? option.id : null)
      }}
    />
  )
}
